/**
 * PaymentTermsPage — the company's default payment terms and each customer's override
 * (DEC-326 item 7, Corbin 2026-10-13).
 *
 * Reads and saves GET / PUT /v1/billing/payment-terms (MANAGE_PAYMENT_TERMS: tenant
 * admin and billing manager, never billing staff; the API enforces it, the nav gate in
 * App.tsx is UX). One Save sends the default plus only the customers that changed.
 *
 * Terms are fixed on an invoice when it is SENT, so a change here applies to the next
 * invoice sent, never to one already sent. The page says so, because "I changed the
 * terms and the due date didn't move" is otherwise the first support question.
 */

import React from 'react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { SortableHead } from 'src/components/ui/sortable-head';

import { sortRows, useUrlSort } from '../lib/url-state';
import { usePaymentTerms, useSavePaymentTerms } from '../features/billing/hooks';
import {
  buildSaveBody,
  draftFrom,
  MAX_TERMS_DAYS,
  parseTermsDays,
  type Draft,
} from '../features/billing/payment-terms';
import { formatPaymentTerms } from '../lib/timestamp';
import { BillDateSection } from '../features/billing/BillDateSection';

const helpStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-text-secondary)',
};

/** The columns the customer terms table sorts by (OPEN-204); the editable Terms column does not. */
const TERMS_SORTS = ['customer'] as const;

export function PaymentTermsPage(): React.JSX.Element {
  const { data, isLoading, isError, refetch } = usePaymentTerms();
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [savedNote, setSavedNote] = React.useState<string | null>(null);
  // OPEN-204 — before the early returns below (hooks run on every render).
  const { sort, toggle } = useUrlSort(TERMS_SORTS, { key: 'customer', dir: 'asc' }, []);
  const customers = sortRows(data?.customers ?? [], sort, (c, k) => {
    switch (k) {
      case 'customer':
        return c.name;
    }
  });

  // Start (and restart after a save) from the server's copy.
  React.useEffect(() => {
    if (data !== undefined) setDraft(draftFrom(data));
  }, [data]);

  const save = useSavePaymentTerms((result) => {
    setDraft(draftFrom(result));
    setSavedNote('Saved. New terms apply to the next invoice you send.');
  });

  if (isLoading || (data !== undefined && draft === null)) {
    return (
      <p role="status" style={{ padding: '24px', color: 'var(--color-text-secondary)' }}>
        Loading payment terms…
      </p>
    );
  }
  if (isError || data === undefined || draft === null) {
    return (
      <div style={{ padding: '24px', color: 'var(--color-danger)' }}>
        <p style={{ marginBottom: '8px' }}>Couldn&apos;t load payment terms.</p>
        <Button
          variant="outline"
          onClick={(): void => {
            void refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const result = buildSaveBody(data, draft);
  const invalid = new Set(result.kind === 'invalid' ? result.fields : []);
  const parsedDefault = parseTermsDays(draft.defaultDays);
  const defaultLabel =
    typeof parsedDefault === 'number'
      ? formatPaymentTerms(parsedDefault)
      : formatPaymentTerms(data.defaultPaymentTermsDays);

  const edit = (next: Draft): void => {
    setSavedNote(null);
    setDraft(next);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '760px' }}>
      <p style={{ ...helpStyle, marginBottom: '20px' }}>
        Terms set each invoice&apos;s due date: the day it&apos;s sent plus the number of days.
        They&apos;re fixed when an invoice is sent, so changes here apply to the next invoice you
        send, not to ones already sent.
      </p>

      <section style={{ marginBottom: '28px' }}>
        <label
          htmlFor="default-terms"
          style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}
        >
          Company default (days)
        </label>
        <Input
          id="default-terms"
          inputMode="numeric"
          value={draft.defaultDays}
          aria-invalid={invalid.has('default')}
          aria-describedby="default-terms-help"
          onChange={(e): void => edit({ ...draft, defaultDays: e.target.value })}
          style={{ maxWidth: '140px' }}
        />
        <p id="default-terms-help" style={{ ...helpStyle, marginTop: '6px' }}>
          {invalid.has('default')
            ? `Enter a whole number from 0 to ${String(MAX_TERMS_DAYS)}.`
            : `${defaultLabel}. Enter 0 for "due on receipt".`}
        </p>
      </section>

      <section>
        <h2 style={{ fontWeight: 600, marginBottom: '6px' }}>Customers</h2>
        <p style={{ ...helpStyle, marginBottom: '10px' }}>
          Leave a customer blank to use the company default.
        </p>
        {data.customers.length === 0 ? (
          <p style={helpStyle}>No active customers yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="customer" sort={sort} onSort={toggle}>
                  Customer
                </SortableHead>
                <TableHead style={{ width: '200px' }}>Terms (days)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c) => {
                const id = c.haulerOperatorId;
                const bad = invalid.has(id);
                return (
                  <TableRow key={id}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>
                      <Input
                        aria-label={`Terms in days for ${c.name}`}
                        inputMode="numeric"
                        placeholder={`Default (${defaultLabel})`}
                        value={draft.overrides[id] ?? ''}
                        aria-invalid={bad}
                        onChange={(e): void =>
                          edit({
                            ...draft,
                            overrides: { ...draft.overrides, [id]: e.target.value },
                          })
                        }
                      />
                      {bad && (
                        <span style={{ fontSize: '12px', color: 'var(--color-danger)' }}>
                          0 to {MAX_TERMS_DAYS}, or blank
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </section>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
        <Button
          type="button"
          disabled={result.kind !== 'ok' || save.isPending}
          onClick={(): void => {
            if (result.kind === 'ok') save.mutate(result.body);
          }}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {save.isError && (
          <span role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
            Couldn&apos;t save. Nothing was changed; try again.
          </span>
        )}
        {savedNote !== null && !save.isError && (
          <span role="status" style={helpStyle}>
            {savedNote}
          </span>
        )}
      </div>

      {/* Hauler billing gap 6 (0250) — the day each ticket is billed on (PR A review L3). */}
      <BillDateSection />
    </div>
  );
}
